import { queryClient } from "@/lib/react-query"
import { axiosReq } from "@/lib/axios";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMemo } from "react";

export type Template = {
  template_id: number;
  template_name: string;
  template_description: string;
  image_id: number;
  image_url: string;
  template_type: number
};


type ImageUploadArgs = {
  catalogue: string,
  user: string,
  media_file: File,
  prompt: string
}

export const useGetTemplates = () => {
  const request = async (): Promise<{ data: Template[] }> => {
    const response = await axiosReq.get('/api/asset/get-templates/')
    return response.data;
  }

  const { data: templatesData } = useQuery({
    queryKey: ["templates"],
    queryFn: request
  })

  return { templatesData }
}

export const useUploadTemplateImage = () => {

  const request = async (data: ImageUploadArgs): Promise<{
    image_path: string,
    image_id: number
  }> => {

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await axiosReq.post('/api/approval/upload-post-media/', data, config);
    return response.data;
  };

  const { mutate: uploadImage, data, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      toast.success("Image uploaded successfully");
      return data;
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error("Image upload error:", error);
    }
  });

  return { uploadImage, isSuccess, data, isPending };
};

export type Catalog = {
  image_url: string
  name: string,
  unique_identifier: string,
}

export const useGetCatalog = (open: boolean) => {

  const request = async (): Promise<{
    catalogue: Catalog[]
  }> => {
    const response = await axiosReq.get('/api/landing/catalogue/')
    return response.data
  }

  const { data: catalogData } = useQuery({
    queryKey: ["catalog"],
    queryFn: request,
    enabled: open
  })

  return { catalogData }
}


export const useSaveImage = () => {
  const request = async (data: { event_id: number, image_id: number }) => {
    const response = await axiosReq.post('/api/approval/save-image-post/', data);
    return response.data
  }

  const { mutate: saveImage, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      queryClient.invalidateQueries({ queryKey: ["themes"] })
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
    }
  })

  return { saveImage, status }
}


type UploadTemplateArgs = { event_id: number, image_id: number, image: File }

export const useSaveImageTemplate = (onSave?: (image: { image_id: number, image_url: string }, index: number) => void, index?: number) => {

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  const request = async (data: UploadTemplateArgs): Promise<{
    data: {
      image_id: number,
      image_url: string,
    }
  }> => {
    const response = await axiosReq.post('/api/asset/save-template-image/', data, config)
    return response.data;
  }

  const { mutateAsync: uploadTemplate, status: saveTemplateStatus } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      console.log("save image templates ", data)
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
      if (data && onSave && index) {
        onSave(data.data, index)
      }
    }
  })

  return { uploadTemplate, saveTemplateStatus };
}

export const useCreateTemplateImageForNonEditable = () => {

  const request = async ({ template_id, event_id }
    : { template_id: number, event_id: number })
    : Promise<{ data: { image_id: number, image_url: string } }> => {
    const response = await axiosReq.post('/api/asset/create-template-image/', { template_id, event_id });
    return response.data;
  }

  const { mutate: generateNewTemplate, status, data: generatedTemplate } = useMutation({
    mutationFn: request,
  })

  return { generateNewTemplate, status, generatedTemplate }
}


export const useCreateNewTemplateImage = (onGenerate: (id: number, url: string) => void) => {

  const request = async ({ template_id, event_id }
    : { template_id: number, event_id: number })
    : Promise<{ data: { image_id: number, image_url: string } }> => {
    const response = await axiosReq.post('/api/asset/create-template-image/', { template_id, event_id });
    return response.data;
  }

  const { mutate: generateNewTemplate, status, data: generatedTemplate } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {
        onGenerate(data.data.image_id, data.data.image_url)
      }
    }
  })

  return { generateNewTemplate, status, generatedTemplate }
}

export const useGetSingleImage = (id: number | undefined) => {

  const request = async (): Promise<{
    image_id: number,
    image_url: string,
    prompt: string
  }> => {
    const response = await axiosReq.post('/api/asset/get-single-image/',
      { image_id: id ? id : 0 });
    return response.data
  }

  const { data: imageData, refetch } = useQuery({
    queryKey: ["single-image", id],
    queryFn: request,
    enabled: !!id
  })

  return { imageData, refetch }
}

export type ImageBody = {
  image_url: string;
  catalogue_item: string;
  id: number;
  prompt: string;
  wishlist: boolean;
  post_id: string[];
};

export type CatalogImageBody = {
  catalogue: string;
  images: ImageBody[];
};

export const useGetGallery = (open: boolean, catalogue: string | undefined) => {
  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody;
  }> => {
    const response = await axiosReq.post('/api/landing/gallery-catalogue/', {
      page: pageParam,
      catalogue: catalogue
    });
    return response.data;
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["gallery-catalogue", catalogue],
    queryFn: ({ pageParam = 1 }) => request(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const hasImages = lastPage.data?.images.length > 0;
      if (!hasImages) return undefined;
      return allPages.length + 1;
    },
    enabled: open && !!catalogue, // fetch only if open is true and catalogue is provided
  });

  const gallery = useMemo(() => {
    if (!data) return [];

    // Since only one catalogue is returned, flatten all pages' images into a single array
    const allImages: ImageBody[] = data.pages.flatMap(page => page.data.images);

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  };
};



export const useSaveImageEdit = () => {

  const request = async ({ image_id, image_file }: { image_id: number, image_file: string }): Promise<{
    image_id: number,
    new_url: string
  }> => {
    const response = await axiosReq.post('/api/asset/save-image/', { image_id, mask: image_file })

    return response.data
  }

  const { mutateAsync: saveImageEdit, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      // refresh theme details data, scheduled posts
      queryClient.invalidateQueries({ queryKey: ["gallery-catalogue"] })
      queryClient.invalidateQueries({ queryKey: ["gallery"] })
      queryClient.invalidateQueries({ queryKey: ["wishlist"] })
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
    }
  })

  return { saveImageEdit, status }
}

export const useUndoEditImage = () => {

  const request = async (id: number): Promise<{
    image_id: number,
    reverted_url: string
  }> => {
    const response = await axiosReq.post('/api/asset/undo-edit-image/', {
      image_id: id
    })

    return response.data;
  }

  const { mutate: undoImage, status: undoStatus } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
    }
  })

  return { undoImage, undoStatus }
}

